import { useParams, Link } from 'react-router-dom';
import PageSEO from '@/components/base/PageSEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <p className="text-foreground-600">文章未找到</p>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const otherArticles = articles
    .filter((a) => a.category !== article.category)
    .slice(0, 2);

  const recommendedArticles = [...relatedArticles, ...otherArticles].slice(0, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    url: `https://naruto-fancreations.com/article/${article.slug}`,
    articleBody: article.content.replace(/<[^>]*>/g, ''),
  };

  return (
    <>
      <PageSEO
        title={`${article.title} - 火影忍者同人创作指南`}
        description={article.excerpt}
        canonicalUrl={`https://naruto-fancreations.com/article/${article.slug}`}
        ogType="article"
        keywords={article.tags.join(',')}
        jsonLd={jsonLd}
      />
      <main className="pt-20">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <article className="mt-6">
              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Link
                    to={`/category/${article.category}`}
                    className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium"
                  >
                    {article.categoryName}
                  </Link>
                  <span className="text-foreground-400 text-xs">{article.date}</span>
                  <span className="text-foreground-400 text-xs">{article.readTime}</span>
                </div>
                <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-3 text-sm text-foreground-600">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                </div>
              </header>

              <div className="mb-8 rounded-lg overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-auto object-cover max-h-[400px]"
                />
              </div>

              <div
                className="prose-content text-foreground-800 text-base leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>

            {recommendedArticles.length > 0 && (
              <section className="mt-14 pt-10 border-t border-background-200/70">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
                  推荐阅读
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {recommendedArticles.map((a) => (
                    <ArticleCard key={a.slug} article={a} variant="compact" />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>
    </>
  );
}